import pmPpcService from '@/services/pmPpcService';
import {
  averagePpcFromHistory,
  countMilestoneChips,
  isActiveMilestoneForCockpit,
  isCockpitFullView,
  openConstraintsCount,
  summarizeMilestoneCommitments,
} from '@/utils/cockpitDisplay';
import { calculateMilestoneDisplayStatus } from '@/utils/milestoneDisplay';

function enrichMilestones(milestones = [], commitments = []) {
  return milestones.map((milestone) => {
    const display = calculateMilestoneDisplayStatus(
      milestone.targetDate || milestone.dueDate,
      milestone.actualDate,
    );
    const enriched = {
      ...milestone,
      ampel: display.ampel,
      deviation: display.deviation,
    };
    const isActive = isActiveMilestoneForCockpit(enriched);
    return {
      ...enriched,
      isActive,
      isCurrent: enriched.ampel === 'yellow' || enriched.ampel === 'red',
      commitments: isActive
        ? summarizeMilestoneCommitments(commitments, milestone.id || milestone._id)
        : null,
    };
  });
}

export default {
  namespaced: true,

  state: {
    loading: false,
    error: null,
    ppcLevel: 'project',
    ppcPeriod: 'week',
    ppcEntityId: '',
    ppcData: null,
    breakdown: null,
    breakdownLoading: false,
    drawerOpen: false,
  },

  getters: {
    loading: (state) => state.loading,
    error: (state) => state.error,
    ppcLevel: (state) => state.ppcLevel,
    ppcPeriod: (state) => state.ppcPeriod,
    ppcEntityId: (state) => state.ppcEntityId,
    ppcData: (state) => state.ppcData,
    current: (state) => state.ppcData?.current || state.ppcData?.currentWeek || null,
    history: (state) => state.ppcData?.history || [],
    teams: (state) => state.ppcData?.teams || [],
    persons: (state) => state.ppcData?.persons || [],
    thresholds: (state) => state.ppcData?.thresholds || { greenMin: 80, yellowMin: 60 },
    inactiveUsersExcluded: (state) => state.ppcData?.inactiveUsersExcluded ?? 0,
    averagePpc: (state, getters) => averagePpcFromHistory(getters.history),
    chartBars: (state, getters) => getters.history.map((entry) => ({
      label: entry.label,
      ppc: entry.ppc,
      n: entry.n,
      periodKey: entry.periodKey || entry.yearWeek,
    })),
    drawerOpen: (state) => state.drawerOpen,
    breakdown: (state) => state.breakdown,
    breakdownLoading: (state) => state.breakdownLoading,
    isFullView(_state, _getters, _rootState, rootGetters) {
      return isCockpitFullView({
        user: rootGetters.getUser,
        selectedProject: rootGetters['pmProject/selectedProject'],
      });
    },
    enrichedMilestones(_state, _getters, _rootState, rootGetters) {
      return enrichMilestones(
        rootGetters['pmMilestone/milestones'] || [],
        rootGetters['pmCommitment/commitments'] || [],
      );
    },
    milestoneChips(_state, getters) {
      return countMilestoneChips(getters.enrichedMilestones);
    },
    openConstraints(_state, _getters, _rootState, rootGetters) {
      return rootGetters['pmConstraint/openConstraints'] || [];
    },
    openConstraintsTotal(_state, getters) {
      return openConstraintsCount(getters.openConstraints);
    },
    constraintsSummary(_state, _getters, _rootState, rootGetters) {
      return rootGetters['pmConstraint/summary'];
    },
  },

  mutations: {
    SET_LOADING(state, loading) {
      state.loading = loading;
    },
    SET_ERROR(state, error) {
      state.error = error;
    },
    SET_PPC_LEVEL(state, level) {
      state.ppcLevel = level;
    },
    SET_PPC_PERIOD(state, period) {
      state.ppcPeriod = period;
    },
    SET_PPC_ENTITY_ID(state, entityId) {
      state.ppcEntityId = entityId || '';
    },
    SET_PPC_DATA(state, data) {
      state.ppcData = data;
    },
    SET_DRAWER_OPEN(state, open) {
      state.drawerOpen = Boolean(open);
    },
    SET_BREAKDOWN(state, breakdown) {
      state.breakdown = breakdown;
    },
    SET_BREAKDOWN_LOADING(state, loading) {
      state.breakdownLoading = loading;
    },
    RESET(state) {
      state.loading = false;
      state.error = null;
      state.ppcLevel = 'project';
      state.ppcPeriod = 'week';
      state.ppcEntityId = '';
      state.ppcData = null;
      state.breakdown = null;
      state.breakdownLoading = false;
      state.drawerOpen = false;
    },
  },

  actions: {
    async loadPage({ commit, dispatch, rootGetters, getters }) {
      const contextReady = rootGetters['pmProject/userProjectContextReady'];
      const projectId = rootGetters['pmProject/selectedProjectId'];
      if (!contextReady || !projectId) {
        commit('RESET');
        commit('pmCqi/RESET', null, { root: true });
        commit('pmTbi/RESET', null, { root: true });
        commit('pmCommitmentQuality/RESET', null, { root: true });
        return null;
      }

      commit('SET_LOADING', true);
      commit('SET_ERROR', null);

      try {
        if (!getters.isFullView) {
          const userId = rootGetters.getUser?._id
            ? String(rootGetters.getUser._id)
            : '';
          commit('SET_PPC_LEVEL', 'person');
          commit('SET_PPC_ENTITY_ID', userId);
          commit('SET_PPC_PERIOD', 'week');
          commit('pmCqi/RESET', null, { root: true });
          commit('pmTbi/RESET', null, { root: true });
          commit('pmCommitmentQuality/RESET', null, { root: true });
        }

        const fetches = [
          dispatch('pmMilestone/setProjectContext', projectId, { root: true }),
          dispatch('pmConstraint/setProjectContext', projectId, { root: true }),
          dispatch('pmCommitment/setProjectContext', projectId, { root: true }),
          dispatch('fetchPpc'),
        ];
        if (getters.isFullView) {
          fetches.push(dispatch('pmCqi/fetchCqi', {}, { root: true }));
          fetches.push(dispatch('pmTbi/fetchTbi', {}, { root: true }));
          fetches.push(dispatch('pmCommitmentQuality/fetchCommitmentQuality', {}, { root: true }));
        }

        await Promise.all(fetches);
        return true;
      } catch (error) {
        commit('SET_ERROR', error?.message || 'Failed to load cockpit');
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async fetchPpc({ commit, state, rootGetters }) {
      const projectId = rootGetters['pmProject/selectedProjectId'];
      if (!projectId) {
        commit('SET_PPC_DATA', null);
        return null;
      }

      const scope = state.ppcLevel || 'project';
      let scopeId = null;
      if (scope === 'team' || scope === 'person') {
        scopeId = state.ppcEntityId || null;
        if (!scopeId) {
          commit('SET_PPC_DATA', null);
          return null;
        }
      }

      const data = await pmPpcService.getProjectPpc(projectId, {
        scope,
        scopeId,
        period: state.ppcPeriod || 'week',
        weeks: 8,
        months: 4,
      });
      commit('SET_PPC_DATA', data);

      if (scope === 'project' && !state.ppcEntityId) {
        const firstTeam = data?.teams?.[0]?.id;
        const firstPerson = data?.persons?.[0]?.id;
        if (state.ppcLevel === 'team' && firstTeam) {
          commit('SET_PPC_ENTITY_ID', firstTeam);
        } else if (state.ppcLevel === 'person' && firstPerson) {
          commit('SET_PPC_ENTITY_ID', firstPerson);
        }
      }

      return data;
    },

    async setPpcLevel({ commit, dispatch, state }, level) {
      commit('SET_PPC_LEVEL', level);
      if (level === 'project') {
        commit('SET_PPC_ENTITY_ID', '');
      } else if (level === 'team') {
        const firstTeam = state.ppcData?.teams?.[0]?.id || '';
        commit('SET_PPC_ENTITY_ID', firstTeam);
      } else if (level === 'person') {
        const firstPerson = state.ppcData?.persons?.[0]?.id || '';
        commit('SET_PPC_ENTITY_ID', firstPerson);
      }
      return dispatch('fetchPpc');
    },

    async setPpcPeriod({ commit, dispatch }, period) {
      commit('SET_PPC_PERIOD', period);
      return dispatch('fetchPpc');
    },

    async setPpcEntityId({ commit, dispatch }, entityId) {
      commit('SET_PPC_ENTITY_ID', entityId);
      return dispatch('fetchPpc');
    },

    async openPpcDrawer({ commit, dispatch }) {
      commit('SET_DRAWER_OPEN', true);
      return dispatch('fetchBreakdown');
    },

    closePpcDrawer({ commit }) {
      commit('SET_DRAWER_OPEN', false);
    },

    async fetchBreakdown({ commit, rootGetters }) {
      const projectId = rootGetters['pmProject/selectedProjectId'];
      if (!projectId) {
        commit('SET_BREAKDOWN', null);
        return null;
      }
      commit('SET_BREAKDOWN_LOADING', true);
      try {
        const data = await pmPpcService.getPpcBreakdown(projectId);
        commit('SET_BREAKDOWN', data);
        return data;
      } finally {
        commit('SET_BREAKDOWN_LOADING', false);
      }
    },
  },
};
